import React, { useRef, useState, ChangeEvent } from 'react';

export type TournamentSponsor = {
  tempId?: string;
  id?: string;
  nome: string;
  link?: string | null;
  descricao?: string | null;
  fotoUrl?: string | null;
};

type Props = {
  value: TournamentSponsor;
  onChange: (patch: Partial<TournamentSponsor>) => void;
  onUploadPhoto?: (file: File) => Promise<string>;
  onUploadError?: (err: unknown) => void;
  onDelete?: () => void;
  busy?: boolean;
  errors?: { nome?: string; link?: string; descricao?: string };
  readOnly?: boolean;
  className?: string;
};

const MAX_DESC = 500;

const TournamentSponsorItem: React.FC<Props> = ({
  value,
  onChange,
  onUploadPhoto,
  onUploadError,
  onDelete,
  busy = false,
  errors,
  readOnly = false,
  className,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [hover, setHover] = useState(false);
  const [uploading, setUploading] = useState(false);
  const disabled = readOnly || busy || uploading;

  const handleField = (field: keyof TournamentSponsor) => (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    onChange({ [field]: e.target.value });
  };

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (onUploadPhoto) {
      setUploading(true);
      try {
        const url = await onUploadPhoto(file);
        onChange({ fotoUrl: url });
      } catch (err) {
        // Bug #19: dá ao componente um canal de feedback de erro para o caller
        // (apresentação fica no caller, ex.: onNotify da aba liga). console.error
        // mantido como log de diagnóstico (não-supressão silenciosa).
        onUploadError?.(err);
        console.error(err);
      } finally {
        setUploading(false);
      }
    } else {
      const preview = URL.createObjectURL(file);
      onChange({ fotoUrl: preview });
      // Upload real deve ser tratado na página
    }
  };

  const imgSrc = value.fotoUrl || '/assets/images/avatar.jpg';
  const descLength = value.descricao?.length || 0;

  return (
    <div
      className={`card p-3 position-relative d-flex flex-column flex-md-row gap-3 ${
        className || ''
      } ${disabled ? 'opacity-75' : ''}`}
      aria-busy={uploading || busy}
    >
      {(uploading || busy) && (
        <div className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-50 d-flex align-items-center justify-content-center rounded">
          <div className="spinner-border text-primary" role="status" />
        </div>
      )}

      {/* Photo */}
      <div
        className="position-relative"
        style={{ width: 120, height: 120, flex: '0 0 auto' }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <img
          src={imgSrc}
          alt={`Foto do patrocinador ${value.nome || ''}`}
          className="w-100 h-100 rounded"
          style={{ objectFit: 'cover' }}
        />
        {!readOnly && (
          <>
            <input
              type="file"
              accept="image/*"
              className="d-none"
              ref={fileInputRef}
              onChange={handleFile}
              disabled={disabled}
            />
            {(hover && !disabled) && (
              <button
                type="button"
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50 border-0 rounded"
                onClick={() => fileInputRef.current?.click()}
                aria-label="Alterar foto"
                title="Alterar foto"
              >
                <i className="ti-camera text-white fs-4" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Fields */}
      <div className="flex-grow-1 w-100">
        <div className="mb-3">
          <label className="form-label">Nome *</label>
          <input
            type="text"
            className={`form-control ${errors?.nome ? 'is-invalid' : ''}`}
            value={value.nome}
            onChange={handleField('nome')}
            disabled={disabled}
            required
            minLength={2}
            readOnly={readOnly}
          />
          {errors?.nome && <div className="invalid-feedback">{errors.nome}</div>}
        </div>
        <div className="mb-3">
          <label className="form-label">Link</label>
          <input
            type="url"
            className={`form-control ${errors?.link ? 'is-invalid' : ''}`}
            value={value.link || ''}
            onChange={handleField('link')}
            disabled={disabled}
            readOnly={readOnly}
            placeholder="https://"
          />
          {errors?.link && <div className="invalid-feedback">{errors.link}</div>}
        </div>
        <div className="mb-3">
          <label className="form-label">Descrição</label>
          <textarea
            className={`form-control ${errors?.descricao ? 'is-invalid' : ''}`}
            value={value.descricao || ''}
            onChange={handleField('descricao')}
            disabled={disabled}
            readOnly={readOnly}
            rows={3}
            maxLength={MAX_DESC}
          />
          <div className="d-flex justify-content-between">
            {errors?.descricao && (
              <div className="invalid-feedback d-block">
                {errors.descricao}
              </div>
            )}
            <small className="text-muted ms-auto">{descLength}/{MAX_DESC}</small>
          </div>
        </div>
      </div>

      {onDelete && (
        <button
          type="button"
          id={`delete-sponsor-${value.tempId}`}
          className="btn btn-outline-danger btn-sm position-absolute top-0 end-0 m-2"
          onClick={() => onDelete?.()}
          disabled={disabled}
          aria-label="Deletar patrocinador"
          title="Deletar patrocinador"
        >
          <i className="bi bi-trash" />
        </button>
      )}
    </div>
  );
};

export default TournamentSponsorItem;

